import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LOG_KINDS,
  LOG_LEVELS,
  LOG_PROVIDERS,
  SINCE_WINDOWS,
  type LogLevel,
  type LogQuery,
  type SinceWindow,
} from '@/types/logs';

interface Props {
  query: LogQuery;
  onChange(patch: Partial<LogQuery>): void;
  onRefresh(): void;
  isBusy: boolean;
}

const SEGMENT_SOURCE: ReadonlyArray<{ value: LogQuery['source']; label: string }> = [
  { value: 'both', label: 'Both' },
  { value: 'sentry', label: 'Sentry' },
  { value: 'posthog', label: 'PostHog' },
];

export function LogsFilterBar({ query, onChange, onRefresh, isBusy }: Props): JSX.Element {
  const { t } = useTranslation();
  const [localQ, setLocalQ] = useState(query.q);
  const debounceRef = useRef<number | null>(null);

  // Sync external query.q into local state when the page resets (e.g. after
  // Refresh discards a draft). Skip while the user is mid-typing.
  useEffect(() => {
    if (debounceRef.current === null) setLocalQ(query.q);
  }, [query.q]);

  function setQ(next: string): void {
    setLocalQ(next);
    if (debounceRef.current !== null) {
      window.clearTimeout(debounceRef.current);
    }
    debounceRef.current = window.setTimeout(() => {
      debounceRef.current = null;
      onChange({ q: next });
    }, 300);
  }

  function toggle<T extends string>(value: T, key: 'provider' | 'kind' | 'level'): void {
    const current = (key === 'level' ? query.level : key === 'provider' ? query.provider : query.kind) as readonly string[];
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    onChange({ [key]: next } as Partial<LogQuery>);
  }

  return (
    <div className="admin-logs-filterbar">
      <div className="admin-logs-filterbar__row">
        <fieldset className="admin-logs-segmented" aria-label={t('admin.logs.filter.source')}>
          <legend className="muted">{t('admin.logs.filter.source')}</legend>
          {SEGMENT_SOURCE.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`admin-logs-pill${query.source === opt.value ? ' admin-logs-pill--active' : ''}`}
              aria-pressed={query.source === opt.value}
              onClick={(): void => onChange({ source: opt.value })}
            >
              {opt.value === 'both' ? t('admin.logs.filter.both') : opt.label}
            </button>
          ))}
        </fieldset>

        <fieldset className="admin-logs-segmented" aria-label={t('admin.logs.filter.since')}>
          <legend className="muted">{t('admin.logs.filter.since')}</legend>
          {SINCE_WINDOWS.map((w) => (
            <button
              key={w}
              type="button"
              className={`admin-logs-pill${query.since === w ? ' admin-logs-pill--active' : ''}`}
              aria-pressed={query.since === w}
              onClick={(): void => onChange({ since: w as SinceWindow })}
            >
              {w}
            </button>
          ))}
        </fieldset>

        <label className="admin-logs-search">
          <span className="muted">{t('admin.logs.filter.search')}</span>
          <input
            type="search"
            placeholder={t('admin.logs.filter.searchPlaceholder')}
            value={localQ}
            onChange={(e): void => setQ(e.target.value)}
            aria-label={t('admin.logs.filter.searchAria')}
          />
        </label>

        <button
          type="button"
          className="btn"
          onClick={onRefresh}
          disabled={isBusy}
          aria-label={t('admin.logs.filter.refreshAria')}
        >
          {isBusy ? t('admin.common.refreshing') : t('admin.common.refresh')}
        </button>
      </div>

      <div className="admin-logs-filterbar__row">
        <fieldset className="admin-logs-segmented" aria-label={t('admin.logs.filter.provider')}>
          <legend className="muted">{t('admin.logs.filter.provider')}</legend>
          {LOG_PROVIDERS.map((p) => (
            <button
              key={p}
              type="button"
              className={`admin-logs-pill${query.provider.includes(p) ? ' admin-logs-pill--active' : ''}`}
              aria-pressed={query.provider.includes(p)}
              onClick={(): void => toggle(p, 'provider')}
            >
              {p}
            </button>
          ))}
        </fieldset>

        <fieldset className="admin-logs-segmented" aria-label={t('admin.common.kind')}>
          <legend className="muted">{t('admin.common.kind')}</legend>
          {LOG_KINDS.map((k) => (
            <button
              key={k}
              type="button"
              className={`admin-logs-pill${query.kind.includes(k) ? ' admin-logs-pill--active' : ''}`}
              aria-pressed={query.kind.includes(k)}
              onClick={(): void => toggle(k, 'kind')}
            >
              {k}
            </button>
          ))}
        </fieldset>

        <fieldset className="admin-logs-segmented" aria-label={t('admin.logs.filter.level')}>
          <legend className="muted">{t('admin.logs.filter.level')}</legend>
          {LOG_LEVELS.map((l) => (
            <button
              key={l}
              type="button"
              className={`admin-logs-pill${query.level.includes(l) ? ' admin-logs-pill--active' : ''}`}
              aria-pressed={query.level.includes(l)}
              onClick={(): void => toggle(l as LogLevel, 'level')}
            >
              {l}
            </button>
          ))}
        </fieldset>
      </div>
    </div>
  );
}
